import React, { useState, useEffect, useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ConsumptionChart from "./ConsumptionChart";
import "./Consumption.css";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { sysConfigApi } from "../../../../services/api/sysconfigService";
import { useTimeseriesSysconfig } from "../../hooks/useTimeseriesSysconfig";
import { listDevicesFromSysconfig } from "../../../devices/domain/deviceModel";
import {
  buildConsumptionFlowRows,
  consumptionDeviceCardTotal,
} from "../Summary/cardFlowRows";
import CardFlowRows from "../Summary/CardFlowRows.jsx";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";
import {
  buildDemandChargePayload,
  sumDeviceAttributionForPeriod,
} from "../../utils/demandCharge";

export default function Consumption() {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { exportedData } = useExportedData();
  const [devices, setDevices] = useState([]);
  const [customer, setCustomer] = useState(null);
  const token = sessionStorage.getItem("accessToken");
  const deviceType = "consumption";
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const { timeSeriesFileName } = useNavigation();

  /* The site's demand charge and the daily-summary rows behind it. Same hook
   * the Summary tab uses, so both pages agree on whether this site is billed a
   * demand charge and on what the figure for a period is. */
  const { hasDemandCharge, dailyDemand } = useDemandChargeData();

  const accountname = sessionStorage.getItem("storageAccountName");

  const {
    storageAccount,
    microgridName,
    datasetName: resolvedDatasetName,
    loading: paramsLoading,
  } = useTimeseriesSysconfig();

  useEffect(() => {
    const email =
      sessionStorage.getItem("selectedCustomerEmail") ||
      sessionStorage.getItem("email");

    if (email) {
      setCustomer(email);
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);

  useEffect(() => {
    if (customer && token && resolvedDatasetName && !paramsLoading) {
      fetchDevices();
    }
  }, [customer, token, resolvedDatasetName, paramsLoading]);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const data = await sysConfigApi({
        accountname,
        simulationName: microgridName,
        datasetName: resolvedDatasetName,
        token,
      });

      const allDevices = listDevicesFromSysconfig(data);
      const consumptionDevices = allDevices
        .filter((d) => d.type === "consumption")
        .map((d) => d.raw);
      setDevices(consumptionDevices);
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadingStateChange = (loading) => {
    setIsChartLoading(loading);
  };

  const StatsCardsSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="cons-sticky-cards">
        <div className="cons-card-container">
          <div className="cons-stats-container consumption-skeleton-card">
            <Skeleton width={120} height={24} style={{ marginBottom: "8px" }} />
            <Skeleton
              width={150}
              height={18}
              style={{ marginBottom: "20px" }}
            />
            <Skeleton width={80} height={16} style={{ marginBottom: "8px" }} />
            <div className="cons-cost-display">
              <Skeleton width={180} height={48} />
            </div>
            <div className="cons-stats" style={{ marginTop: "20px" }}>
              <div className="cons-stat-item">
                <Skeleton
                  width={80}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={60} height={20} />
              </div>
              <div className="cons-stat-item">
                <Skeleton
                  width={90}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={70} height={20} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );

  /* The period the cards describe, as the ISO keys ConsumptionChart stamps
   * beside the display label. */
  const period = useMemo(
    () => ({
      view: exportedData?.periodView || "",
      selDate: exportedData?.selDate || "",
      selMonth: exportedData?.selMonth || "",
      selYear: exportedData?.selYear || "",
    }),
    [exportedData],
  );

  const demandData = useMemo(
    () =>
      buildDemandChargePayload(
        hasDemandCharge,
        dailyDemand,
        period.view,
        period.selDate,
        period.selMonth,
        period.selYear,
      ),
    [hasDemandCharge, dailyDemand, period],
  );

  /* The engine reports consumption for the site as a whole, not per meter, so
   * every card carries the same site-wide figures. That was true before this
   * change too; it is called out here rather than left to be inferred. The
   * demand figure added below is the same kind of site figure and inherits the
   * same caveat -- with more than one consumption device each card would repeat
   * the category attribution, exactly as `price`, `maxPower` and
   * `totalAreaUnderCurve` already do, until the engine writes a per-device
   * consumption column for `deviceDemand` to pick up.
   *
   * Values are passed through untouched. They used to be defaulted with
   * `|| "NA"`, which turned a genuine measured 0 into a missing value -- the
   * inverse of the bug this branch exists to fix. Presence is decided once, by
   * summaryCardReadiness's finite check, further down. */
  const deviceCards = devices.map((device) => ({
    DeviceName: device.name || "NA",
    status: device.status || "NA",
    price: exportedData.totalCost,
    maxPower: exportedData.maxPower,
    totalAreaUnderCurve: exportedData.totalAreaUnderCurve,
    /* Listening for a per-device consumption attribution column that the engine
     * does not write today. It is here rather than omitted so that the day the
     * sim starts attributing demand to individual loads -- the sysconfig schema
     * already carries `controllable_loads` -- these cards populate with no
     * change to the webapp. Until then it is null and the demand row falls back
     * to the category figure, which IS this device's figure while building load
     * is the whole consumption category. See consumptionDeviceDemandRow. */
    deviceDemand: (() => {
      const raw = sumDeviceAttributionForPeriod(
        dailyDemand,
        device.name,
        period.view,
        period.selDate,
        period.selMonth,
        period.selYear,
      );
      return raw === null ? null : raw.toFixed(2);
    })(),
    /* Carried through for the Load row's money, the same way price and maxPower
     * already are -- and with the same caveat, which is worth stating because
     * the card looks per-device: the engine writes building load per METER, not
     * per consumption device, so every card on this page shows the site figure.
     * That predates this change and applies to all four of these fields
     * equally; it is not something the money column introduces. */
    loadCost: exportedData.loadCost,
  }));

  return (
    <div className="cons-container">
      <div className="sticky-header">
        <h1>Consumption</h1>
      </div>
      {isChartLoading || loading ? (
        <StatsCardsSkeleton />
      ) : (
        <div className="cons-sticky-cards">
          <div
            className={`cons-card-container ${
              devices.length > 3 ? "horizontal-scroll" : ""
            }`}
          >
            {deviceCards.map((item, index) => {
              /* The card's NET: the engine's Consumption Cost plus this card's
               * demand charge, formed by the same helper and the same two named
               * figures as the Summary tab's Consumption card. The two pages
               * show one quantity, so they compute it one way. */
              const { total, includesDemand } = consumptionDeviceCardTotal(
                item.price,
                item,
                demandData,
              );

              return (
                <div
                  key={index}
                  className="card consumption-card"
                  style={
                    devices.length > 3
                      ? { minWidth: "620px", maxWidth: "620px" }
                      : {}
                  }
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5
                        className="card-title mb-0"
                        style={{
                          cursor: "pointer",
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={
                          item.DeviceName.length > 25 ? item.DeviceName : ""
                        }
                      >
                        {item.DeviceName.length > 25
                          ? `${item.DeviceName.substring(0, 25)}...`
                          : item.DeviceName}
                      </h5>
                      <div className="box-transfer">
                        <span className="period-consumption">
                          {exportedData.view === "All Time View"
                            ? "All Time View"
                            : `${exportedData.view}: ${exportedData.selectedPeriod}`}
                        </span>
                      </div>
                    </div>

                    <CardFlowRows
                      rows={buildConsumptionFlowRows(item, demandData, {
                        perDevice: true,
                      })}
                      total={total}
                      totalIncludesDemand={includesDemand}
                      testIdPrefix={`consumption-device-${index}`}
                      showColumnHeaders
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ConsumptionChart
        csvData={timeSeriesFileName}

        onLoadingStateChange={handleLoadingStateChange}
      />
    </div>
  );
}
